import { useCallback, useState } from 'react';
import { Button, InlineMessage, Flex, Select } from '@bigcommerce/big-design';
import ModalConfirmation from './ModalConfirmation';
import './OrdersTable.css';
import OrderStatusColumn from './OrderStatusColumn';
import OrderTagColumn from './OrderTagColumn';
import CopyableText from '../../../../components/CopyableText';
import Table from '../../../../components/Table';
import { useClient } from '../../../../context/client';
import { useOrderStatusOptions } from '../../../../context/OrderStatuses';
import { formatOrderDate } from '../../../../utils/formatOrderDate';
import { NewTabButton, ViewOrderButton, CopyOrderNumberButton } from '../../../../utils/OrderButtons';
import { usePagination } from '../hooks/usePagination';
import { FILTERS } from '../hooks/useSearchFilters';


function useDownloadOrders() {
	const [isDownloading, setIsDownloading] = useState(false);
	const client = useClient();

	const dispatch = useCallback(
		async (orderIds) => {
			setIsDownloading(true);

			try {
				await client.downloadOrders(orderIds);
			} finally {
				setIsDownloading(false);
			}
		},
		[client],
	);

	return [isDownloading, dispatch];
}

const tableColumns = [
	{
		header: 'View',
		width: '40px',
		hash: 'view_order',
		render: ({ bc_order_id }) => (
			<ViewOrderButton orderId={bc_order_id} />
		),
	},
	{
		header: 'Open',
		width: '40px',
		hash: 'new_tab',
		render: ({ bc_order_id }) => (
			<NewTabButton orderId={bc_order_id} />
		),
	},
	{
		header: 'Order',
		hash: 'bc_order_id',
		render: ({ bc_order_id }) => (
			<CopyOrderNumberButton orderId={bc_order_id} />
		),
	},
	{
		header: 'Customer',
		hash: 'customer_name',
		render: ({ customer_name }) => <CopyableText text={customer_name} />,
	},
	{
		header: 'Order Date',
		hash: 'date',
		render: ({ date }) => <CopyableText text={formatOrderDate(date)} />,
	},
	{
		header: 'Est. Ship Date',
		hash: 'estimatedShippingDate',
		render: ({ estimatedShippingDate }) => <CopyableText text={estimatedShippingDate} />,
	},
	{
		header: 'Vehicles',
		hash: 'vehicles',
		render: ({ vehicles }) => (
			<CopyableText text={vehicles?.filter(v => v && v.trim()).join(', ')} />
		),
	},
	{
		header: 'Product',
		hash: 'product',
		render: ({ product }) => (
			<CopyableText text={product} />
		),
	},
	{
		header: 'Color',
		hash: 'color',
		render: ({ color }) => <CopyableText text={color} />,
	},
	{
		header: 'Total',
		hash: 'total',
		render: ({ total }) => `$${parseFloat(total).toFixed(2)}`,
	},
	{
		header: 'Vendor',
		hash: 'vendor',
		render: ({ vendor }) => vendor,
	},
	{
		header: 'Status',
		hash: 'status',
		render: ({ status }) => <OrderStatusColumn status={status} />,
	},
	{
		header: 'Photos',
		hash: 'photos',
		render: ({ photos }) => (photos ? 'Yes' : 'No'),
	},
	{
		header: 'Tags',
		hash: 'tags',
		render: ({ tags }) => <OrderTagColumn tags={tags || []} />,
		isSortable: true,
	},
];

export default function OrdersTable(props) {
	const {
		orders,
		totalOrderCount,
		setFilter,
		isLoading,
		selectedItems,
		setSelectedItems,
		fetchOrdersList,
		sortField,
		sortOrder,
		activeTab,
	} = props;

	const client = useClient();
	const [isUpdatingTags, setIsUpdatingTags] = useState(false);
	const [statusOptionSelected, setStatusOptionSelected] = useState(null);
	const [isOpen, setIsOpen] = useState(false);
	const statusOptions = useOrderStatusOptions();
	const paginationOptions = usePagination(setFilter, totalOrderCount);
	const [isDownloading, downloadOrders] = useDownloadOrders();
	const [newSelectedItems, setNewSelectedItems] = useState([]);
	const openModal = () => {
		const items = [...selectedItems];
		items.map((item) => (item.new_status = statusOptionSelected));
		setNewSelectedItems(items);
		setIsOpen(true);
	};

	const handleRemoveInReview = async () => {
		setIsUpdatingTags(true);
		try {
			await client.updateOrderTags(
				selectedItems.map(({ bc_order_id }) => bc_order_id),
				'remove',
				'In Review',
			);
			await fetchOrdersList();
			setSelectedItems([]);
		} catch (e) {
			console.error(e);
		} finally {
			setIsUpdatingTags(false);
		}
	};

	const handleMoveToInReview = async () => {
		setIsUpdatingTags(true);
		try {
			await client.updateOrderTags(
				selectedItems.map(({ bc_order_id }) => bc_order_id),
				'add',
				'In Review',
			);
			await fetchOrdersList();
			setSelectedItems([]);
		} catch (e) {
			console.error(e);
		} finally {
			setIsUpdatingTags(false);
		}
	};

	const handleSort = useCallback((columnHash, direction) => {
		if (columnHash === sortField) {
			if (sortOrder === 'asc') {
				setFilter(FILTERS.SORT_FIELD, columnHash);
				setFilter(FILTERS.SORT_ORDER, 'desc');
			} else if (sortOrder === 'desc') {
				// Instead of removing, set to default values
				setFilter(FILTERS.SORT_FIELD, 'date');
				setFilter(FILTERS.SORT_ORDER, 'desc');
			} else {
				setFilter(FILTERS.SORT_FIELD, columnHash);
				setFilter(FILTERS.SORT_ORDER, 'asc');
			}
		} else {
			setFilter(FILTERS.SORT_FIELD, columnHash);
			setFilter(FILTERS.SORT_ORDER, 'asc');
		}
	}, [setFilter, sortField, sortOrder]);

	return (
		<>
			<Table
				isLoading={isLoading}
				columns={tableColumns}
				items={orders}
				selectable={{
					selectedItems,
					onSelectionChange: setSelectedItems,
				}}
				pagination={paginationOptions}
				stickyHeader
				sortable={{
					columnHash: sortField || 'date',
					direction: sortOrder || 'desc',
					onSort: handleSort,
				}}
				actions={
					selectedItems.length > 0 && (
						<Flex marginLeft="large">
							<Button
								isLoading={isDownloading}
								disabled={isDownloading}
								onClick={() =>
									downloadOrders(
										selectedItems.map(
											({ bc_order_id }) => bc_order_id,
										),
									)
								}
							>
								Download
							</Button>
							<Flex marginLeft="large">
								<Select
									placeholder="Update Status"
									onOptionChange={(value) =>
										setStatusOptionSelected(value)
									}
									value={statusOptionSelected || ''}
									options={statusOptions}
								/>
								<Button
									variant="secondary"
									marginLeft="small"
									disabled={statusOptionSelected === null}
									onClick={() => openModal()}
								>
									Update
								</Button>
							</Flex>
							<Flex
								marginLeft="large"
								alignItems="center"
								style={{
									borderLeft: '1px solid #D9D9D9',
									paddingLeft: '1rem',
								}}
							>
								{activeTab !== 'In Review' && (
									<Button
										variant="secondary"
										marginRight="small"
										isLoading={isUpdatingTags}
										disabled={isUpdatingTags}
										onClick={handleMoveToInReview}
									>
										Move to "In Review"
									</Button>
								)}
								{activeTab !== 'New' && (
									<Button
										variant="secondary"
										isLoading={isUpdatingTags}
										disabled={isUpdatingTags}
										onClick={handleRemoveInReview}
									>
										Remove "In Review" Tag
									</Button>
								)}
							</Flex>
						</Flex>
					)
				}
				emptyComponent={
					<InlineMessage
						type="info"
						messages={[{ text: 'No Orders match this filter.' }]}
						marginVertical="medium"
					/>
				}
			/>
			<ModalConfirmation
				items={newSelectedItems}
				isOpen={isOpen}
				setIsOpen={setIsOpen}
				statusOptionSelected={statusOptionSelected}
				fetchOrdersList={fetchOrdersList}
			/>
		</>
	);
}